import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const RunningProfitChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        let cumulative = 0;
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const net = metrics?.net ?? 0;
                cumulative += net;
                return { date, cumulative };
            });
    }, [finance?.dailyBreakdown]);

    const finalValue = useMemo(
        () => (chartData.length > 0 ? chartData[chartData.length - 1].cumulative : 0),
        [chartData]
    );

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: finalValue >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}>
                {formatCompactNumber(finalValue, currencySymbol)}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    const isPositive = finalValue >= 0;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                <defs>
                    <linearGradient id="runningProfitGreen" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.revenue} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={CHART_COLORS.revenue} stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="runningProfitRed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.expense} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={CHART_COLORS.expense} stopOpacity={0.02} />
                    </linearGradient>
                </defs>
                <CartesianGrid
                    vertical={false}
                    stroke={CHART_STYLES.grid.stroke}
                    strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                    strokeWidth={CHART_STYLES.grid.strokeWidth}
                    opacity={CHART_STYLES.grid.opacity}
                />
                <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                    {...CHART_STYLES.axis}
                />
                <YAxis
                    tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                    {...CHART_STYLES.axis}
                />
                <Tooltip
                    contentStyle={CHART_STYLES.tooltip.contentStyle}
                    cursor={CHART_STYLES.tooltip.cursor}
                    itemStyle={CHART_STYLES.tooltip.itemStyle}
                    formatter={(value: number) => [formatCurrency(value, currencySymbol), t('charts.cumulativeProfit', 'Cumulative Profit')]}
                    labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                />
                <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                <Area
                    type="monotone"
                    dataKey="cumulative"
                    name={t('charts.cumulativeProfit', 'Cumulative Profit')}
                    stroke={isPositive ? CHART_COLORS.revenue : CHART_COLORS.expense}
                    strokeWidth={CHART_STYLES.line.strokeWidth}
                    fill={isPositive ? 'url(#runningProfitGreen)' : 'url(#runningProfitRed)'}
                    dot={false}
                    activeDot={CHART_STYLES.line.activeDot}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};
